'use client'

import { useState, useEffect, FormEvent, useMemo } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { useTheme } from 'next-themes'
import { usePathname, useRouter } from 'next/navigation'
import Logo from './Logo'
import HeaderTopCategories from './HeaderTopCategories'
import HeaderBottomStrip, { type HeaderStripItem } from './HeaderBottomStrip'
import { useAuth } from './AuthProvider'
import { cn, getImageUrl } from '@/lib/utils'
import { IMAGE_PRESETS } from '@/lib/image-presets'
import { buildSearchPageUrl } from '@/lib/search-url'
import {
  saveSearchLabel,
  getRecentSearches,
  clearRecentSearches,
} from '@/lib/search-display'
import { SITE_HEADER_ROW, SITE_MAX, SITE_PAD_X, SITE_SHELL_TOP_STRIP } from '@/lib/site-layout'
import ThemeToggleSwitch from './ThemeToggleSwitch'
import { LiveBeaconIcon } from './LiveBadge'
import { sortHeaderCategories } from '@/lib/sort-site-categories'

interface Category {
  id: number
  title: string
  slug: string
  color?: string | null
  cat_sort?: number
}

interface HeaderProps {
  headerCategories: Category[]
  stripItems: HeaderStripItem[]
  menuCategories: Category[]
  categoryColor?: string | null
  showShortsNav?: boolean
}

export default function Header({
  headerCategories,
  stripItems,
  menuCategories,
  categoryColor,
  showShortsNav = true,
}: HeaderProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  const { theme, setTheme, systemTheme } = useTheme()
  const [mounted, setMounted] = useState(false)
  const pathname = usePathname()
  const router = useRouter()
  const { user, isLoading: authLoading, isPremium } = useAuth()

  const sidebarCategories = useMemo(() => {
    const source = headerCategories.length > 0 ? headerCategories : menuCategories
    return sortHeaderCategories(source)
  }, [headerCategories, menuCategories])

  const headerBorderColor = useMemo(() => {
    if (categoryColor) return categoryColor
    if (pathname === '/') return '#D92E2E'

    const categorySlug = pathname.split('/').filter(Boolean)[0]
    if (!categorySlug) return '#D92E2E'

    const category =
      sidebarCategories.find((cat) => cat.slug === categorySlug) ??
      menuCategories.find((cat) => cat.slug === categorySlug)

    return category?.color || '#D92E2E'
  }, [categoryColor, pathname, sidebarCategories, menuCategories])

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!isSearchOpen) return
    setRecentSearches(getRecentSearches())
  }, [isSearchOpen])

  const openSearch = () => {
    setIsMenuOpen(false)
    setIsSearchOpen(true)
  }

  // Menü veya arama açıkken arka plan kaydırmasını kapat
  useEffect(() => {
    if (!isMenuOpen && !isSearchOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [isMenuOpen, isSearchOpen])

  // ESC: arama veya menüyü kapat
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (isSearchOpen) setIsSearchOpen(false)
      if (isMenuOpen) {
        setIsMenuOpen(false)
        setIsSearchOpen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isSearchOpen, isMenuOpen])

  const toggleMenu = () => {
    setIsSearchOpen(false)
    setIsMenuOpen((v) => !v)
  }

  const closeMenu = () => {
    setIsMenuOpen(false)
    setIsSearchOpen(false)
  }

  const submitSearch = (query: string) => {
    const trimmed = query.trim()
    if (trimmed.length < 2) return

    setIsSearchOpen(false)
    setIsMenuOpen(false)
    setSearchQuery('')
    saveSearchLabel(trimmed)
    router.push(buildSearchPageUrl(trimmed))
  }

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submitSearch(searchQuery)
  }

  const toggleTheme = () => {
    // Simple toggle: light ↔ dark
    const effectiveTheme = theme === 'system' ? systemTheme : theme
    setTheme(effectiveTheme === 'dark' ? 'light' : 'dark')
  }

  const getThemeIcon = () => {
    if (!mounted) return null
    const effectiveTheme = theme === 'system' ? systemTheme : theme
    
    // Show moon icon in light mode (click to go dark), sun icon in dark mode (click to go light)
    if (effectiveTheme === 'dark') {
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
        </svg>
      )
    } else {
      return (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )
    }
  }

  const getThemeLabel = () => {
    if (!mounted) return 'Gündüz'
    const effectiveTheme = theme === 'system' ? systemTheme : theme
    return effectiveTheme === 'dark' ? 'Gece' : 'Gündüz'
  }

  const currentTheme = mounted && theme === 'system' ? systemTheme : theme

  return (
    <>
      {/* Ana üst çubuk: logo + admin kategorileri */}
      <div className="fixed top-0 left-0 right-0 z-50 w-full min-w-0">
        <div
          className="header box-border flex h-14 w-full min-w-0 items-center border-b-[3px] border-solid bg-white md:h-[4.125rem] dark:bg-slate-950"
          style={{ borderBottomColor: headerBorderColor }}
        >
          <div
            className={cn(
              SITE_HEADER_ROW,
              SITE_PAD_X,
            )}
          >
            <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-hidden sm:gap-1.5 md:gap-2">
              <button
                type="button"
                onClick={toggleMenu}
                className="flex size-8 shrink-0 touch-manipulation items-center justify-center rounded-md text-gray-700 sm:size-9 md:size-10 dark:text-gray-200"
                aria-label="Menüyü Aç"
                aria-expanded={isMenuOpen}
              >
                <svg className="size-4 sm:size-5 md:size-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <Link href="/" className="min-w-0 shrink-0 overflow-hidden" aria-label="Haber.com Ana Sayfa">
                <Logo className="h-5 w-auto max-w-[7rem] min-w-0 sm:h-7 sm:max-w-[10rem] md:h-8 md:max-w-[12rem] lg:h-9 lg:max-w-[14rem]" />
              </Link>
              {sidebarCategories.length > 0 ? (
                <div className="relative min-w-0 flex-1 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  <HeaderTopCategories categories={sidebarCategories} />
                </div>
              ) : null}
            </div>

            <div className="flex shrink-0 items-center gap-0.5 sm:gap-1.5 md:gap-2">
              <button
                type="button"
                onClick={openSearch}
                className="flex size-8 shrink-0 touch-manipulation items-center justify-center rounded-md text-gray-700 sm:size-9 md:size-10 dark:text-gray-200"
                aria-label="Ara"
                aria-expanded={isSearchOpen}
              >
                <svg className="size-4 sm:size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </button>

              <Link
                href="/canli-tv"
                title="Canlı TV"
                aria-label="Canlı TV"
                className="header-canli-tv flex h-8 shrink-0 touch-manipulation items-center gap-1 rounded-md px-1.5 text-[#D92E2E] transition-colors hover:bg-red-50 sm:h-9 sm:px-2 md:h-10 dark:hover:bg-red-950/30"
              >
                <LiveBeaconIcon />
                <span className="live-tv-text">TV</span>
              </Link>

            {/* Tema: hydrate öncesi aynı genişlik — CLS azaltır */}
            <div className="hidden sm:flex h-10 shrink-0 items-center justify-center">
              {mounted ? (
                <button
                  onClick={toggleTheme}
                  className="flex items-center gap-1 md:gap-1.5 text-gray-900 dark:text-gray-100 px-1.5 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                  aria-label={`Tema: ${getThemeLabel()}`}
                  title={`Tema: ${getThemeLabel()}`}
                >
                  <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                  </svg>
                  <ThemeToggleSwitch isDark={currentTheme === 'dark'} size="md" />
                  <svg className="w-4 h-4 text-indigo-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                </button>
              ) : (
                <div
                  className="h-9 w-16 rounded-lg bg-gray-100/90 dark:bg-slate-800/60"
                  aria-hidden
                />
              )}
            </div>

            {/* Üye alanı */}
            <div className="hidden md:flex h-10 shrink-0 items-center justify-end gap-2">
              {authLoading ? (
                <div
                  className="h-9 w-9 animate-pulse rounded-full bg-gray-200 dark:bg-slate-700"
                  aria-hidden
                />
              ) : user ? (
                <>
                <Link
                  href="/account"
                  className="relative flex size-9 shrink-0 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 hover:text-[#D92E2E] dark:text-gray-200 dark:hover:bg-slate-800"
                  aria-label="Hesabım"
                  title={user.username || user.name || user.email || 'User'}
                >
                  {user.avatar ? (
                    <Image
                      src={getImageUrl(user.avatar, IMAGE_PRESETS.avatar) || user.avatar}
                      alt="User Avatar"
                      width={36}
                      height={36}
                      className="size-9 rounded-full object-cover"
                      unoptimized
                    />
                  ) : (
                    <div className="flex size-9 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-orange-500 text-sm font-bold text-white">
                      {(user.username || user.name || user.email || 'U').charAt(0).toUpperCase()}
                    </div>
                  )}
                  {isPremium && (
                    <span className="absolute -right-0.5 -top-0.5 size-2.5 rounded-full border-2 border-white bg-yellow-400 dark:border-slate-900" title="Premium Üye" />
                  )}
                </Link>
                </>
              ) : (
                <Link
                  href="/login"
                  className="flex size-9 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 hover:text-[#D92E2E] dark:text-gray-200 dark:hover:bg-slate-800"
                  aria-label="Üye Girişi"
                  title="Üye Girişi"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </Link>
              )}
            </div>
            </div>
          </div>
        </div>
      </div>

      <HeaderBottomStrip items={stripItems} />

      {/* Arama overlay */}
      {isSearchOpen && (
        <>
          <div
            className="fixed inset-0 z-[55] bg-black/60"
            onClick={() => setIsSearchOpen(false)}
            aria-hidden
          />

          <div
            role="dialog"
            aria-modal="true"
            aria-label="Haber ara"
            className={cn(
              'fixed inset-x-0 top-0 z-[60] flex justify-center px-3 sm:px-4',
              SITE_SHELL_TOP_STRIP,
            )}
          >
            <div className="w-full min-w-0 max-w-xl animate-[slideIn_0.2s_ease-out]">
              <div className="overflow-hidden rounded-xl border border-gray-200/90 bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.35)] dark:border-slate-700 dark:bg-slate-900 sm:rounded-2xl">
                <div className="flex items-center gap-2 border-b border-gray-100 bg-gradient-to-r from-red-50/80 to-white px-3 py-2.5 dark:border-slate-800 dark:from-red-950/30 dark:to-slate-900 sm:gap-2.5 sm:px-4 sm:py-3">
                  <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#D92E2E] text-white shadow-sm sm:size-9 sm:rounded-xl">
                    <svg className="size-4 sm:size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-bold text-gray-900 sm:text-sm dark:text-white">Haber ara</p>
                    <p className="truncate text-[10px] text-gray-500 sm:text-[11px] dark:text-slate-400">
                      Başlık, kategori veya etiket
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsSearchOpen(false)}
                    className="flex size-8 shrink-0 touch-manipulation items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 sm:size-9 dark:hover:bg-slate-800 dark:hover:text-gray-200"
                    aria-label="Aramayı kapat"
                  >
                    <svg className="size-4 sm:size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form
                  toolname="search_news_overlay"
                  tooldescription="Haber.com’da başlık, kategori veya etiket ile haber ara; sonuç sayfasına yönlendir."
                  toolautosubmit=""
                  onSubmit={handleSearchSubmit}
                  className="px-3 pb-2 pt-3 sm:px-4 sm:pt-4"
                >
                  <div className="search-field flex flex-col gap-2 rounded-xl border border-gray-200 bg-gray-50 p-1.5 transition-colors focus-within:border-[#D92E2E]/45 focus-within:bg-white focus-within:ring-2 focus-within:ring-[#D92E2E]/15 dark:border-slate-700 dark:bg-slate-800 dark:focus-within:border-red-500/40 dark:focus-within:bg-slate-900 sm:flex-row sm:items-center sm:gap-2 sm:rounded-2xl sm:p-2">
                    <div className="flex min-w-0 flex-1 items-center gap-2 px-2 sm:px-2.5">
                      <svg
                        className="size-4 shrink-0 text-[#D92E2E]/80 sm:size-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <input
                        type="search"
                        name="q"
                        inputMode="search"
                        enterKeyHint="search"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Ara…"
                        className="min-w-0 flex-1 appearance-none border-0 bg-transparent py-2 text-sm text-gray-900 shadow-none outline-none ring-0 placeholder:text-gray-400 focus:outline-none focus:ring-0 dark:text-white dark:placeholder:text-slate-500 sm:py-2.5 sm:text-[15px] md:text-base"
                        autoFocus
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        aria-label="Arama metni"
                        toolparamdescription="Aranacak haber, kategori veya etiket metni"
                      />
                      {searchQuery ? (
                        <button
                          type="button"
                          onClick={() => setSearchQuery('')}
                          className="flex size-7 shrink-0 items-center justify-center rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-700 sm:size-8 dark:hover:bg-slate-700 dark:hover:text-gray-200"
                          aria-label="Temizle"
                        >
                          <svg className="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      ) : null}
                    </div>
                    <button
                      type="submit"
                      disabled={searchQuery.trim().length < 2}
                      className="w-full touch-manipulation rounded-lg bg-[#D92E2E] px-4 py-2 text-[13px] font-bold text-white transition-colors hover:bg-[#c42727] disabled:cursor-not-allowed disabled:opacity-45 sm:w-auto sm:shrink-0 sm:rounded-xl sm:px-5 sm:py-2.5 sm:text-sm"
                    >
                      Ara
                    </button>
                  </div>
                  {searchQuery.trim().length > 0 && searchQuery.trim().length < 2 ? (
                    <p className="mt-2 px-0.5 text-[11px] text-amber-700 dark:text-amber-400">
                      En az 2 karakter girin
                    </p>
                  ) : null}
                </form>

                <div className="max-h-[min(50dvh,20rem)] space-y-3 overflow-y-auto overscroll-y-contain px-3 pb-3 sm:max-h-[min(55vh,22rem)] sm:space-y-4 sm:px-4 sm:pb-4">
                  {recentSearches.length > 0 ? (
                    <div>
                      <div className="mb-1.5 flex items-center justify-between gap-2 sm:mb-2">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-gray-400 sm:text-[11px] dark:text-slate-500">
                          Son aramalar
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            clearRecentSearches()
                            setRecentSearches([])
                          }}
                          className="text-[10px] font-medium text-gray-400 transition-colors hover:text-[#D92E2E] sm:text-[11px] dark:text-slate-500 dark:hover:text-red-400"
                        >
                          Temizle
                        </button>
                      </div>
                      <ul className="flex flex-col gap-0.5">
                        {recentSearches.map((q) => (
                          <li key={q}>
                            <button
                              type="button"
                              onClick={() => submitSearch(q)}
                              className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-gray-800 transition-colors hover:bg-red-50 hover:text-[#D92E2E] sm:gap-2.5 sm:rounded-xl sm:px-2.5 sm:py-2 sm:text-[13px] dark:text-slate-200 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                            >
                              <svg className="size-3.5 shrink-0 text-gray-400 sm:size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="min-w-0 flex-1 truncate font-medium">{q}</span>
                              <svg className="size-3 shrink-0 text-gray-300 sm:size-3.5 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  <div>
                    <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.06em] text-gray-400 sm:mb-2 sm:text-[11px] dark:text-slate-500">
                      Hızlı erişim
                    </p>
                    <div className="flex flex-wrap gap-1.5 sm:gap-2">
                      {[
                        { href: '/son-dakika', label: 'Son Dakika' },
                        ...sidebarCategories.slice(0, 6).map((c) => ({
                          href: `/${c.slug}`,
                          label: c.title,
                        })),
                      ].map((item) => (
                        <Link
                          key={item.href}
                          href={item.href}
                          onClick={() => setIsSearchOpen(false)}
                          className="max-w-full truncate rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[12px] font-medium text-gray-700 transition-colors hover:border-[#D92E2E]/35 hover:bg-red-50 hover:text-[#D92E2E] sm:px-3 sm:py-1.5 sm:text-[13px] dark:border-slate-700 dark:bg-slate-800 dark:text-gray-300 dark:hover:border-red-800 dark:hover:bg-red-950/40 dark:hover:text-red-400"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="hidden items-center justify-between gap-2 border-t border-gray-100 px-4 py-2.5 text-[11px] text-gray-400 sm:flex dark:border-slate-800 dark:text-slate-500">
                  <span className="inline-flex items-center gap-1.5">
                    <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500 dark:bg-slate-800 dark:text-gray-400">
                      Enter
                    </kbd>
                    ara
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <kbd className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] text-gray-500 dark:bg-slate-800 dark:text-gray-400">
                      Esc
                    </kbd>
                    kapat
                  </span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Menu Overlay — blur yok: backdrop-filter animasyonu ciddi kasma yapar */}
      <div
        className={`fixed inset-0 z-50 bg-black/55 transition-opacity duration-200 ease-out motion-reduce:transition-none ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={closeMenu}
        aria-hidden={!isMenuOpen}
      />

      {/* Panel: GPU transform + içerik her zaman DOM'da (açılışta mount patlaması yok) */}
      <div
        role="dialog"
        aria-modal={isMenuOpen ? true : undefined}
        aria-hidden={!isMenuOpen}
        aria-label="Site menüsü"
        inert={!isMenuOpen}
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-full max-w-[min(100%,17.5rem)] flex-col overflow-hidden bg-white pl-[max(0px,env(safe-area-inset-left,0px))] pt-[max(0px,env(safe-area-inset-top,0px))] text-gray-900 shadow-2xl transform-gpu transition-transform duration-200 ease-[cubic-bezier(0.32,0.72,0,1)] motion-reduce:transition-none sm:max-w-[18rem] dark:bg-slate-950 dark:text-white',
          isMenuOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
        )}
      >
            {/* Üst: hesap + kapat */}
            <div className="flex shrink-0 items-center justify-between gap-2 bg-white px-3 py-2 dark:bg-slate-900">
              {user ? (
                <Link href="/account" onClick={closeMenu} className="flex min-w-0 items-center gap-2 rounded-lg py-0.5 pr-1 transition-colors hover:bg-gray-50 dark:hover:bg-slate-800">
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-[#D92E2E] text-[11px] font-bold text-white">
                    {(user.username || user.name || user.email || 'U').charAt(0).toUpperCase()}
                  </div>
                  <span className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">{user.username || user.name || user.email || 'User'}</span>
                </Link>
              ) : (
                <Link href="/login" onClick={closeMenu} className="flex min-w-0 items-center gap-2 rounded-lg py-0.5 pr-1 transition-colors hover:bg-gray-50 dark:hover:bg-slate-800">
                  <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-red-50 dark:bg-red-900/30">
                    <svg className="size-3.5 text-[#D92E2E]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <span className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">Giriş Yap / Üye Ol</span>
                </Link>
              )}
              <button
                onClick={closeMenu}
                className="flex size-7 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-800 dark:hover:bg-slate-800 dark:hover:text-white"
                aria-label="Menüyü Kapat"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            {/* Arama — ikonlu tek alan */}
            <div className="shrink-0 bg-white px-3 pb-3 dark:bg-slate-900">
              <form
                toolname="search_news_menu"
                tooldescription="Mobil menüden Haber.com’da haber ara; sonuç sayfasına yönlendir."
                toolautosubmit=""
                className="flex flex-col gap-2"
                onSubmit={handleSearchSubmit}
              >
                <div className="relative">
                  <svg className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="search"
                    name="q"
                    inputMode="search"
                    enterKeyHint="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Ara…"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 shadow-none outline-none ring-0 focus:border-[#D92E2E] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#D92E2E]/15 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder:text-slate-500 dark:focus:bg-slate-800"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    aria-label="Arama metni"
                    toolparamdescription="Aranacak haber, kategori veya etiket metni"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full rounded-lg bg-[#D92E2E] px-3 py-2 text-xs font-bold text-white transition-colors hover:bg-[#C02727]"
                >
                  Ara
                </button>
              </form>
            </div>

            {/* İçerik + alt bar (boşluk yok — içerik bitince alt bar) */}
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain">
              <div className="px-2.5 py-2">
                <nav className="flex flex-col gap-px" aria-label="Ana menü">
                  {[
                    { href: '/', label: 'Anasayfa', d: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
                    { href: '/son-dakika', label: 'Haber', d: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z' },
                    { href: '/video', label: 'Video', d: 'M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z', d2: 'M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
                    ...(showShortsNav
                      ? [{ href: '/shorts', label: 'Shorts', d: 'M7 4h10a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z' }]
                      : []),
                    { href: '/galeri', label: 'Galeri', d: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z' },
                    { href: '/discover', label: 'Keşfet', d: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z' },
                  ].map((item) => {
                    const active = item.href === '/' ? pathname === '/' : pathname.startsWith(item.href)
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={closeMenu}
                        className={cn(
                          'flex items-center gap-2.5 rounded-lg px-2.5 py-[7px] text-[13px] transition-colors',
                          active
                            ? 'bg-red-50 font-semibold text-[#D92E2E] dark:bg-red-950/40 dark:text-red-400'
                            : 'font-medium text-gray-800 hover:bg-gray-50 hover:text-[#D92E2E] dark:text-slate-200 dark:hover:bg-slate-800/80 dark:hover:text-red-400'
                        )}
                      >
                        <svg className={cn('size-4 shrink-0', active ? 'text-[#D92E2E] dark:text-red-400' : 'text-gray-400')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={item.d} />
                          {'d2' in item && item.d2 ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={item.d2} /> : null}
                        </svg>
                        {item.label}
                      </Link>
                    )
                  })}
                </nav>

                <p className="mb-1 mt-3 px-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500">Popüler</p>
                <nav className="flex flex-col gap-px" aria-label="Popüler">
                  {[
                    { href: '/en-cok-okunanlar', label: 'En Çok Okunanlar', d: 'M4 6h16M4 10h16M4 14h16M4 18h16' },
                    { href: '/en-cok-paylasilanlar', label: 'En Çok Paylaşılanlar', d: 'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6' },
                    { href: '/en-cok-izlenenler', label: 'En Çok İzlenenler', d: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z' },
                  ].map((item) => {
                    const active = pathname.startsWith(item.href)
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        onClick={closeMenu}
                        className={cn(
                          'flex items-center gap-2.5 rounded-lg px-2.5 py-[6px] text-[12.5px] transition-colors',
                          active
                            ? 'bg-red-50 font-semibold text-[#D92E2E] dark:bg-red-950/40 dark:text-red-400'
                            : 'font-medium text-gray-600 hover:bg-gray-50 hover:text-[#D92E2E] dark:text-slate-400 dark:hover:bg-slate-800/80 dark:hover:text-red-400'
                        )}
                      >
                        <svg className={cn('size-3.5 shrink-0', active ? 'text-[#D92E2E]' : 'text-gray-400')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d={item.d} />
                        </svg>
                        {item.label}
                      </Link>
                    )
                  })}
                </nav>

                {sidebarCategories.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1.5 px-2.5 text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400 dark:text-slate-500">Kategoriler</p>
                    <div className="flex flex-wrap gap-1.5 px-1">
                      {sidebarCategories.map((cat) => {
                        const active = pathname === `/${cat.slug}` || pathname.startsWith(`/${cat.slug}/`)
                        return (
                          <Link
                            key={cat.id}
                            href={`/${cat.slug}`}
                            onClick={closeMenu}
                            className={cn(
                              'rounded-full border px-2.5 py-1 text-[12px] font-medium uppercase transition-colors',
                              active
                                ? 'border-[#D92E2E]/25 bg-[#D92E2E] text-white'
                                : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-[#D92E2E]/40 hover:bg-white hover:text-[#D92E2E] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:text-red-400'
                            )}
                          >
                            {cat.title}
                          </Link>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-1 border-t border-gray-200/80 bg-white px-3 py-2 pb-[max(0.5rem,env(safe-area-inset-bottom,0px))] dark:border-slate-800 dark:bg-slate-900">
                <div className="mb-2 flex items-center gap-1">
                  <a href="https://www.facebook.com/comhaber" target="_blank" rel="noopener noreferrer" className="flex size-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-[#1877F2] hover:text-white" aria-label="Facebook">
                    <svg className="size-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  </a>
                  <a href="https://x.com/habercomsosyal" target="_blank" rel="noopener noreferrer" className="flex size-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-black hover:text-white" aria-label="X">
                    <svg className="size-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                  </a>
                  <a href="https://www.instagram.com/habercomsosyal" target="_blank" rel="noopener noreferrer" className="flex size-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-[#E1306C] hover:text-white" aria-label="Instagram">
                    <svg className="size-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                  <a href="#" className="flex size-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-[#0088cc] hover:text-white" aria-label="Telegram">
                    <svg className="size-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
                  </a>
                  <a href="https://www.youtube.com/@haber_com" target="_blank" rel="noopener noreferrer" className="flex size-7 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-[#FF0000] hover:text-white" aria-label="YouTube">
                    <svg className="size-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                  </a>
                  {mounted && (
                    <button
                      type="button"
                      onClick={toggleTheme}
                      className="ml-auto flex items-center gap-2 rounded-full bg-gray-100 px-2.5 py-1.5 text-[11px] font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                      aria-label={`Tema: ${getThemeLabel()}`}
                    >
                      <ThemeToggleSwitch isDark={currentTheme === 'dark'} size="sm" />
                      <span>{getThemeLabel()}</span>
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-2.5 gap-y-0.5 text-[10px] text-gray-400 dark:text-slate-500">
                  <Link href="/hakkimizda" onClick={closeMenu} className="hover:text-gray-700 dark:hover:text-slate-300">Hakkımızda</Link>
                  <Link href="/authors" onClick={closeMenu} className="hover:text-gray-700 dark:hover:text-slate-300">Yazarlar</Link>
                  <Link href="/kunye" onClick={closeMenu} className="hover:text-gray-700 dark:hover:text-slate-300">Künye</Link>
                  <Link href="/gizlilik-politikasi" onClick={closeMenu} className="hover:text-gray-700 dark:hover:text-slate-300">Gizlilik</Link>
                  <Link href="/iletisim" onClick={closeMenu} className="hover:text-gray-700 dark:hover:text-slate-300">İletişim</Link>
                </div>
              </div>
            </div>
      </div>
    </>
  )
}
